/**
 * Welche Felder die Regelkonfiguration hat — und wie sie zu bedienen sind.
 *
 * **Eine Beschreibung statt eines handgeschriebenen Formulars.** Rund dreißig Balancewerte
 * einzeln als Eingabefeld zu schreiben wäre dreißig Mal dieselbe Zeile, und jedes neue Feld im
 * Schema müsste an zwei Stellen nachgetragen werden. Hier steht jedes Feld einmal — mit seinem
 * Pfad, seiner Art und seinem Übersetzungsschlüssel —, und **eine** Komponente zeichnet daraus
 * das Formular.
 *
 * **Der eigentliche Gewinn ist die Prüfbarkeit.** `test/admin-fields.test.ts` läuft über alle
 * Blätter von `defaultRuleConfig()` und verlangt zu jedem genau ein Feld. Ein neuer Balancewert im
 * Schema lässt damit einen Test scheitern, statt still unerreichbar zu bleiben — und ein
 * unerreichbarer Balancewert verfehlt den Projektzweck, denn die Software existiert auch, um das
 * Spiel selbst zu prüfen (`CLAUDE.md`).
 *
 * **Hier stehen keine Werte, nur Pfade.** Die Zahlen leben im Schema in
 * `packages/contracts/src/rule-config.ts` (Regel 2) — diese Datei weiß, *dass* es ein Punkteziel
 * gibt, nicht *welches*. Ein Default hier wäre eine zweite Wahrheit und könnte von der ersten
 * abweichen, ohne dass es auffällt.
 *
 * **Die gemeinsamen Figurenfelder entstehen aus `PIECE_TYPES`.** Anzahl und Bewegung hat jede
 * Klasse; sie aus der Laufzeitliste der Engine zu erzeugen heißt, dass eine sechste Figurenklasse
 * sofort bedienbar wäre. Was nur einzelne Klassen haben — Schlagreichweite, Sprung, die vier
 * Push-Parameter — steht ausdrücklich dabei, weil es ausdrücklich so entschieden ist (§52).
 */

import { PIECE_TYPES, type PieceType } from '@flaghex/engine';

/**
 * Wie ein Feld bedient wird.
 *
 * `nullableInt` und `capture` sind nicht Bequemlichkeit, sondern zwei entschiedene Regeln:
 * `movement.max: null` heißt **unbegrenzt** (§16, der Läufer), und `capture: 'sameAsMovement'`
 * heißt „so weit wie die Bewegung" statt eines zweiten Satzes Zahlen (Läufer und Springer,
 * entschieden 2026-10-02). Beide müssen in der Oberfläche als das erscheinen, was sie sind — eine
 * leere Zahl wäre für den Tester nicht von „noch nicht eingetragen" zu unterscheiden.
 */
export type FieldKind = 'text' | 'int' | 'nullableInt' | 'bool' | 'passage' | 'capture';

export interface RuleField {
  /** Punktpfad in der RuleConfig, wie `admin/paths.ts` ihn versteht. */
  readonly path: string;
  readonly kind: FieldKind;
  /** Schlüssel in `de.admin.labels`. Niemals der deutsche Text selbst (Regel 6). */
  readonly label: string;
  /**
   * Der Pfad, dessen Werte dieses Feld übernimmt, wenn sein Marker abgeschaltet wird.
   *
   * Nur für `capture`, und der Grund ist eine Regel: `'sameAsMovement'` **heißt** „so weit wie
   * die Bewegung". Wer den Marker abschaltet, braucht Zahlen — und die einzigen, die keine
   * Erfindung sind, sind genau die der Bewegung (`CLAUDE.md` Regel 5: keine Spielregel selbst
   * erfinden, und ein stillschweigend gesetzter Default wäre eine). Der Wechsel ist damit
   * bedeutungsgleich und verändert das Balancing nicht.
   */
  readonly mirrors?: string;
  /**
   * Kleinster erlaubter Wert eines Zahlenfeldes — Default **1**.
   *
   * Steht am Feld und nicht im Formular, weil es eine Aussage über **diesen Wert** ist und
   * nicht über Zahlenfelder im Allgemeinen. Bis `V2-2e1` war die Grenze im Formular
   * hartkodiert; `bots.minDeliberationMs` ist das erste Feld, für das die **0** ein gemeinter
   * Wert ist (Nr. 45: „in der Massensimulation 0") — und sie war damit nicht eingebbar,
   * obwohl das Feld vollständig aussah.
   */
  readonly min?: number;
}

export interface RuleSection {
  /** Schlüssel in `de.admin.groups` — oder `undefined`, wenn der Abschnitt eine Figur ist. */
  readonly group?: string;
  /** Die Figurenklasse, deren Anzeigename den Abschnitt benennt. */
  readonly piece?: PieceType;
  readonly fields: readonly RuleField[];
}

/** Die Felder, die jede Figurenklasse hat (§52: Anzahl, movement.min, movement.max). */
function commonPieceFields(type: PieceType): readonly RuleField[] {
  return [
    { path: `pieces.${type}.count`, kind: 'int', label: 'count' },
    { path: `pieces.${type}.movement.min`, kind: 'int', label: 'movementMin' },
    { path: `pieces.${type}.movement.max`, kind: 'nullableInt', label: 'movementMax' },
  ];
}

/** Die Schlagreichweite einer Figur — mit der Bewegung als bedeutungsgleicher Rückfall. */
function captureField(type: PieceType): RuleField {
  return {
    path: `pieces.${type}.capture`,
    kind: 'capture',
    label: 'capture',
    mirrors: `pieces.${type}.movement`,
  };
}

/**
 * Was einzelne Figurenklassen zusätzlich haben — genau nach §52.
 *
 * Der Fähnrich steht bewusst ohne Zusatz da: er schlägt nicht und stößt nicht, also gibt es
 * nichts einzustellen. Eine leere Liste ist hier eine Aussage und kein Vergessen.
 */
const EXTRA_PIECE_FIELDS: Readonly<Record<PieceType, readonly RuleField[]>> = {
  flagBearer: [],
  guard: [captureField('guard')],
  runner: [captureField('runner')],
  jumper: [
    captureField('jumper'),
    { path: 'pieces.jumper.movement.canJump', kind: 'bool', label: 'canJump' },
  ],
  pusher: [
    { path: 'pieces.pusher.push.range.min', kind: 'int', label: 'pushRangeMin' },
    { path: 'pieces.pusher.push.range.max', kind: 'int', label: 'pushRangeMax' },
    { path: 'pieces.pusher.push.distance', kind: 'int', label: 'pushDistance' },
    { path: 'pieces.pusher.push.pusherAdvance', kind: 'int', label: 'pusherAdvance' },
    { path: 'pieces.pusher.push.canPushOffBoard', kind: 'bool', label: 'canPushOffBoard' },
    {
      path: 'pieces.pusher.push.canPushFlagBearerFromGoal',
      kind: 'bool',
      label: 'canPushFlagBearerFromGoal',
    },
  ],
};

/**
 * Der allgemeine Abschnitt — §52 „Allgemein", dazu die vier später entschiedenen Felder.
 *
 * `version` steht zuerst, weil sie an jeder Partie gespeichert wird (§97): wer eine Reihe
 * Testpartien mit veränderten Werten spielt, braucht einen Namen dafür, sonst sind die Daten
 * hinterher nicht zuzuordnen.
 */
const GENERAL_FIELDS: readonly RuleField[] = [
  { path: 'version', kind: 'text', label: 'version' },
  { path: 'board.sideLength', kind: 'int', label: 'sideLength' },
  { path: 'startZone.rows', kind: 'int', label: 'startZoneRows' },
  { path: 'scoring.target', kind: 'int', label: 'scoringTarget' },
  { path: 'scoring.resetScoreOnFlagCapture', kind: 'bool', label: 'resetScoreOnFlagCapture' },
  { path: 'setup.timeLimitMs', kind: 'nullableInt', label: 'setupTimeLimitMs' },
  { path: 'respawn.enabled', kind: 'bool', label: 'respawnEnabled' },
  { path: 'ring.enabled', kind: 'bool', label: 'ringEnabled' },
  { path: 'goalCell.passage', kind: 'passage', label: 'goalCellPassage' },
  // Nr. 45, Zyklus `V2-2e1`. 0 ist erlaubt und gemeint — der Wert für die Massensimulation.
  { path: 'bots.minDeliberationMs', kind: 'int', label: 'botsMinDeliberationMs', min: 0 },
];

/**
 * Die Abschnitte in der Reihenfolge, in der sie erscheinen.
 *
 * Allgemein zuerst, dann die fünf Figurenklassen in der Reihenfolge der Engine. §52 listet die
 * Figuren voran und „Allgemein" zuletzt; umgedreht, weil die Brettgröße darüber entscheidet, ob
 * eine Figurenzahl überhaupt noch auf die Startfelder passt — wer von oben nach unten liest,
 * sollte das vorher gesehen haben.
 */
export const RULE_SECTIONS: readonly RuleSection[] = [
  { group: 'general', fields: GENERAL_FIELDS },
  ...PIECE_TYPES.map((piece) => ({
    piece,
    fields: [...commonPieceFields(piece), ...EXTRA_PIECE_FIELDS[piece]],
  })),
];

/** Alle Felder, flach — die Grundlage der Vollständigkeitsprüfung. */
export const RULE_FIELDS: readonly RuleField[] = RULE_SECTIONS.flatMap(
  (section) => section.fields,
);

/** Die drei Möglichkeiten für `goalCell.passage` (§19 regelt nur das Betreten). */
export const PASSAGE_OPTIONS = ['open', 'jumpersOnly', 'blocked'] as const;
